import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";

import { createAdminAnnouncement } from "../api";
import AdminAnnouncementForm from "../components/Admin/AdminAnnouncementForm";
import PageHero from "../components/common/PageHero";
import { useSnackbarStore } from "../store/useSnackbarStore";

export default function AdminAnnouncementCreatePage() {
  const navigate = useNavigate();
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const [submitting, setSubmitting] = React.useState(false);

  const handleCreate = async (data: { title: string; content: string }) => {
    try {
      setSubmitting(true);
      await createAdminAnnouncement(data);
      showSnackbar("Announcement shared with devotees.", "success");
      navigate("/announcements");
    } catch (error) {
      console.error("Failed to create announcement", error);
      showSnackbar("Unable to save the announcement. Please try again.", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ pb: 2 }}>
      <PageHero
        eyebrow="Inspire seva"
        title="Compose a new announcement"
        description="Craft a heartfelt update that guides devotees toward the latest seva opportunities and celebrations."
        actions={[
          <Button
            key="back"
            variant="plain"
            color="neutral"
            startDecorator={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/announcements")}
          >
            Back to announcements
          </Button>,
        ]}
      />

      <Grid container spacing={2.5} alignItems="stretch">
        <Grid xs={12} lg={7}>
          <AdminAnnouncementForm
            onSubmit={handleCreate}
            submitLabel="Share announcement"
            title="Announcement details"
            helperText="Title and describe the seva moment so devotees immediately understand the opportunity and next steps."
            onCancel={() => navigate("/announcements")}
            isSubmitting={submitting}
          />
        </Grid>
        <Grid xs={12} lg={5}>
          <Stack spacing={2.5} sx={{ height: "100%" }}>
            <Card
              variant="soft"
              color="primary"
              sx={{
                borderRadius: "xl",
                p: { xs: 2.5, md: 3 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                boxShadow: "var(--joy-shadow-md)",
              }}
            >
              <Chip
                size="sm"
                variant="soft"
                color="neutral"
                startDecorator={<CampaignRoundedIcon fontSize="small" />}
                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
              >
                Stewardship tips
              </Chip>
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                Share with clarity and warmth
              </Typography>
              <List
                size="sm"
                sx={{
                  "--List-gap": "0.75rem",
                  "--ListItem-paddingY": "0px",
                }}
              >
                <ListItem>
                  <Typography level="body-sm">
                    Highlight the seva impact in the first sentence so caretakers instantly understand the benefit.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography level="body-sm">
                    Invite devotees to take action with a clear CTA—link to registrations, seva sign ups, or donation forms.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography level="body-sm">
                    Keep the tone devotional and encouraging; every message should feel like a warm personal invitation.
                  </Typography>
                </ListItem>
              </List>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "primary.100" }}>
                <CelebrationRoundedIcon fontSize="small" />
                <Typography level="body-sm">
                  Need inspiration? Revisit past announcements in the library to mirror tone and structure.
                </Typography>
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
